import { Formik } from 'formik'
import { Form } from '../Form/Form'
import { Button } from 'blaze-ui/Button'
import { Header } from 'components/Wizards/ContentPlanning/Header/Header'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Sections } from 'components/Wizards/Wizard/Form/Sections'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { useCallback, useEffect, useState } from 'react'
import { Field as BaseField } from '../Field/Field'
import { useCreateFieldsetCompletion } from 'hooks/queries/workspaces/useCreateFieldsetCompletion'
import { PollFieldsetCompletion } from 'components/PollFieldsetCompletion/PollFieldsetCompletion'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { AIJobStatus } from 'types'
import { TextFieldLoadingState } from '../TextFieldLoadingState/TextFieldLoadingState'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { ProseInput } from 'common/ProseInput/ProseInput'
import InputStyles from 'almanac-ui/TextInputField/Input.module.scss'
import Styles from './ContentPlanWizard.module.scss'
import cx from 'classnames'
import { reportError } from 'utils/error'
import { FilePathFormikField } from '../FilePathFormikField/FilePathFormikField'
import { Plugin } from 'prosemirror-state'

type Values = {
  website: string
  websiteText: string
  topic: string
  description: string
  top_product_features: string
  key_product_benefits: string
  target_customer_personas: string
}

type ValuesKeys = 'description' | 'top_product_features' | 'key_product_benefits' | 'target_customer_personas'

const fieldsSet = [
  { name: 'description', label: 'Product/Company Description' },
  { name: 'top_product_features', label: 'Top Product Features' },
  { name: 'key_product_benefits', label: 'Key Product Benefits' },
  { name: 'target_customer_personas', label: 'Target Customer Personas' },
] as { name: ValuesKeys; label: string }[]

interface Props extends StepProps {
  websiteText: string
  setWebsiteText: (setWebsiteText: string) => void
}

export const ConfirmDetails = multiStep<Props>(
  ({ initialValues, onSubmit, schema, websiteText, setWebsiteText, onBack }) => {
    const { mutate, data } = useCreateFieldsetCompletion()
    const [dataFetched, setDataFetched] = useState(false)
    const [fieldsCompleted, setFieldsCompleted] = useState(false)
    const { isMobile } = useResponsiveDesign()
    const { meta } = schema.describe()

    const handleError = useCallback(() => {
      showErrorMessageToast('Blaze was unable to generate the data. Please contact support if this issue persists.')
      onBack?.()
    }, [onBack])

    const handleBack = () => {
      setWebsiteText('')
      onBack?.()
    }

    // Fetch the company's data once when website's text has been extracted
    useEffect(() => {
      if (!websiteText || dataFetched) return

      setDataFetched(true)
      mutate(
        {
          fields: ['company_name'].concat(fieldsSet.map((field) => field.name)),
          completedFields: {
            topic: websiteText,
          },
          generationType: GenerationType.ContentPlan,
        },
        {
          onError: (error) => {
            reportError(error)
            handleError()
          },
        },
      )
    }, [dataFetched, handleError, mutate, onBack, websiteText])

    return (
      <Formik
        validationSchema={schema}
        onSubmit={onSubmit}
        initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false })}
      >
        {({ submitForm, values, setFieldValue, isSubmitting }) => {
          const fieldValues = values as Values

          return (
            <>
              <PollFieldsetCompletion
                fieldsetCompletionId={data?.fieldset_completion.id}
                onComplete={(fieldset) => {
                  if (fieldset.status === AIJobStatus.Failed) {
                    handleError()
                    return
                  }
                  setFieldsCompleted(true)

                  Object.entries(fieldset.completed_fields).forEach(([key, value]) => {
                    if (key === 'company_name') {
                      setFieldValue('filename', `Content Plan: ${value}`, true)
                    } else {
                      setFieldValue(key, value, true)
                    }
                  })
                }}
              />

              <Form.Root>
                <Sections.Root schema={schema}>
                  <Sections.AutoFillFieldsSection
                    header={
                      <Header
                        header="How did we do?"
                        subHeader="Review and refine the details we’ve generated about your company. Your updates will help us craft a content plan tailored just for you!"
                      />
                    }
                  >
                    {fieldsSet.map((field) => {
                      const lines = field.name === 'description' ? 1 : 4
                      const value = fieldValues[field.name as ValuesKeys]

                      return (
                        <BaseField key={field.name} label={field.label}>
                          {!value && <TextFieldLoadingState lines={lines} />}
                          {!!value && (
                            <ProseInput
                              withBorder
                              initialHtmlString={value}
                              onChange={(html) => setFieldValue(field.name, html, true)}
                              className={cx(InputStyles.input, InputStyles.md, InputStyles.textarea, Styles.inputFont)}
                              plugins={{
                                // No-op plugin so we don't load actual mentions plugin
                                mentions: new Plugin({}),
                              }}
                            />
                          )}
                        </BaseField>
                      )
                    })}

                    <BaseField label="Name your doc and select a location" isRequired>
                      <FilePathFormikField
                        placeholder="Name your doc..."
                        locationPickerFullWidth
                        isNameLoading={!fieldValues['filename' as ValuesKeys]}
                      />
                    </BaseField>
                  </Sections.AutoFillFieldsSection>
                  <Form.Actions isSticky>
                    <Form.PreviousButton onPress={handleBack} isDisabled={isSubmitting}>
                      Back
                    </Form.PreviousButton>
                    <Form.PrimaryActions>
                      <Button
                        size="lg"
                        onPress={submitForm}
                        fullWidth={isMobile}
                        isDisabled={isSubmitting || !fieldsCompleted}
                      >
                        {meta.submitLabel}
                      </Button>
                    </Form.PrimaryActions>
                  </Form.Actions>
                </Sections.Root>
              </Form.Root>
            </>
          )
        }}
      </Formik>
    )
  },
)
